import { useEffect, useRef, useState } from 'react';
import { Bell, Check, ArrowRight } from 'lucide-react';
import { api, dateLabel } from '../lib/api';
import { useCRM } from '../lib/context';

export function NotificationInbox() {
  const { data, navigate, openContact } = useCRM();
  const [open, setOpen] = useState(false),
    [items, setItems] = useState<any[]>([]),
    [error, setError] = useState('');
  const [unread, setUnread] = useState(0),
    [total, setTotal] = useState(0);
  const [cursor, setCursor] = useState(''),
    [history, setHistory] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null),
    [loading, setLoading] = useState(false);
  const [desktop, setDesktop] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted',
  );
  const shown = useRef(new Set<string>()),
    anchor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        setLoading(true);
        const result = await api(
          `/notifications?limit=12${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        );
        if (!active) return;
        setItems(result.notifications);
        setUnread(result.unread);
        setTotal(result.total);
        setNextCursor(result.nextCursor);
        setError('');
        if (desktop && data.settings?.notifications?.desktop)
          for (const item of result.notifications.filter((n: any) => !n.read_at)) {
            if (shown.current.has(item.id)) continue;
            shown.current.add(item.id);
            // No personal record content is placed on the OS lock screen.
            if (Date.now() - Date.parse(item.created_at) < 120000)
              new Notification('CRM work update', { body: item.title, tag: item.id });
          }
      } catch (e: any) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    void load();
    const timer = window.setInterval(load, 30000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [data.user.id, desktop, data.settings?.notifications?.desktop, cursor]);
  useEffect(() => {
    setItems([]);
    setUnread(0);
    setTotal(0);
    setCursor('');
    setHistory([]);
    shown.current.clear();
  }, [data.user.id]);
  useEffect(() => {
    const dismiss = (event: PointerEvent) => {
      if (!anchor.current?.contains(event.target as Node)) setOpen(false);
    };
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', dismiss);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', dismiss);
      document.removeEventListener('keydown', key);
    };
  }, []);
  const read = async (item: any) => {
    try {
      await api(`/notifications/${item.id}/read`, 'POST', {});
      setItems((current) =>
        current.map((n) => (n.id === item.id ? { ...n, read_at: new Date().toISOString() } : n)),
      );
      if (!item.read_at) setUnread((current) => Math.max(0, current - 1));
    } catch (e: any) {
      setError(e.message);
      return;
    }
    setOpen(false);
    if (item.entity === 'contacts') {
      const contact = data.contacts.find((c) => c.id === item.entity_id);
      if (contact) openContact(contact);
      else navigate('contacts');
    } else navigate('tasks');
  };
  const readAll = async () => {
    setLoading(true);
    try {
      await api('/notifications/read-all', 'POST', {});
      const result = await api(
        `/notifications?limit=12${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
      );
      setItems(result.notifications);
      setUnread(result.unread);
      setTotal(result.total);
      setNextCursor(result.nextCursor);
      setError('');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="notification-anchor" ref={anchor}>
      <button
        className="icon-btn notification-button"
        aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <Bell size={19} />
        {unread > 0 && <i />}
      </button>
      {open && (
        <div className="notification-popover" role="region" aria-label="Notification inbox">
          <div className="popover-title">
            <h3>Your work updates</h3>
            <span>{unread} unread</span>
          </div>
          {error && <p role="alert">{error}</p>}
          {loading && <p role="status">Loading updates…</p>}
          {!items.length && !error && !loading && (
            <p>{cursor ? 'No older visible updates on this page.' : 'You’re all caught up.'}</p>
          )}
          {items.map((item) => (
            <button key={item.id} onClick={() => void read(item)}>
              <span className="notification-icon">
                {item.read_at ? <Check size={16} /> : <Bell size={16} />}
              </span>
              <span>
                <strong>{item.title}</strong>
                <small>
                  {dateLabel(item.created_at)}
                  {item.kind === 'daily_digest'
                    ? ` · ${item.metadata.openTasks} open · ${item.metadata.overdueTasks} overdue`
                    : ''}
                </small>
              </span>
              <ArrowRight size={14} />
            </button>
          ))}
          <div className="popover-title" aria-label="Notification pages">
            <button
              className="text-link"
              disabled={loading || !history.length}
              onClick={() => {
                setCursor(history.at(-1) || '');
                setHistory((pages) => pages.slice(0, -1));
              }}
            >
              Newer
            </button>
            <small>
              {total} total · page {history.length + 1}
            </small>
            <button
              className="text-link"
              disabled={loading || !nextCursor}
              onClick={() => {
                setHistory((pages) => [...pages, cursor]);
                setCursor(nextCursor || '');
              }}
            >
              Older
            </button>
          </div>
          {unread > 0 && (
            <button className="text-link" disabled={loading} onClick={() => void readAll()}>
              Mark all visible updates read
            </button>
          )}
          {typeof Notification !== 'undefined' && !desktop && (
            <button
              className="text-link"
              onClick={async () => {
                setDesktop((await Notification.requestPermission()) === 'granted');
              }}
            >
              Enable desktop notifications
            </button>
          )}
        </div>
      )}
    </div>
  );
}
